import React, { useCallback, useEffect, useReducer } from "react"
// Admin App - Main Application Entry Point
import "react-toastify/dist/ReactToastify.css"
import "mapbox-gl/dist/mapbox-gl.css"

import "./index.css"
import "@ciq/shared/css/App.css"
import "@ciq/shared/css/AttributeInput.css"
import "@ciq/shared/css/Button.css"
import "@ciq/shared/css/LoginRedirect.css"
import "@ciq/shared/css/NavBar.css"
import "@ciq/shared/css/SplitLayout.css"
import "@ciq/shared/css/TableView.css"
import "@ciq/shared/css/Components.css"

import "@ciq/shared/css/pages/DashboardPage.css"
import "@ciq/shared/css/pages/MapPage.css"
import "@ciq/shared/css/pages/SchoolPage.css"
import "@ciq/shared/css/pages/SettingsPage.css"
import "@ciq/shared/css/pages/WirelessConfigPage.css"
import "@ciq/shared/css/pages/FileUploadShared.css"

import "./css/App.css"
import "./css/NavBar.css"
import "./css/SchoolPage.css"
import "./css/AlertsPage.css"
import "./css/SparkPage.css"
import "./css/SettingsPage.css"
import "./css/FileUploadPage.css"

import { BrowserRouter, Navigate, Outlet, Route, Routes, useNavigate } from "react-router-dom"
import { useLocation } from "react-router"
import { ToastContainer } from "react-toastify"

import Api from "@ciq/shared/api/api"
import eventBus from "@ciq/shared/hooks/eventBus"

import NavigationBar from "@ciq/shared/components/navbar"
import IdleTimeout from "@ciq/shared/components/idle_timeout"

import MapView from "./pages/map/view"
import AdminConsole from "./pages/admin_console"
import SchoolView from "./pages/school/view"
import WirelessConfigView from "./pages/wireless_config/view"
import AlertsView from "./pages/alerts/view"
import FileUploadView from "./pages/file_upload/view"
import { getRouteTitle } from "./utils/getRouteTitle.js"

import { ReactComponent as AlertIcon } from "@ciq/shared/assets/icons/navbar/alert_icon.svg"
import { ReactComponent as DashboardIcon } from "@ciq/shared/assets/icons/navbar/dashboard_icon.svg"
import { ReactComponent as LogoutIcon } from "@ciq/shared/assets/icons/navbar/logout_icon.svg"
import { ReactComponent as MapIcon } from "@ciq/shared/assets/icons/navbar/map_icon.svg"
import { ReactComponent as SchoolIcon } from "@ciq/shared/assets/icons/navbar/school_icon.svg"
import { ReactComponent as SettingsIcon } from "@ciq/shared/assets/icons/navbar/settings_icon.svg"
import { ReactComponent as WirelessConfigIcon } from "@ciq/shared/assets/icons/navbar/wireless_icon.svg"
import { ReactComponent as FileUploadIcon } from "@ciq/shared/assets/icons/navbar/file_upload_icon.svg"

import SettingsModal from "./components/modals/settings"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { initMap } from "@ciq/shared/hooks/map"

/**
 * Creates a logout handler function.
 * Extracted for testability - can be tested directly without React component rendering.
 *
 * @param {Object} Api - The API module for making HTTP requests
 * @param {Object} eventBus - The event bus for emitting events
 * @param {Function} errorToast - Function to show error toast notifications
 * @returns {Function} A logout handler that calls the logout API and emits logout event
 */
export const createLogoutHandler = (Api, eventBus, errorToast) => () => {
  Api.post(`/auth/logout`, {})
    .catch(e => {
      if (e.handled) return
      console.error(e.error)
      errorToast("Unable to logout")
    })
    .finally(() => {
      eventBus.emit("logout")
    })
}

/**
 * Navigates to the previous school in the list, wrapping to the last school if at the beginning.
 * Extracted for testability.
 *
 * @param {Object|null} currentSchool - The currently selected school object with id property
 * @param {Array} schools - Array of school objects to navigate through
 * @param {Function} setSchool - Function to set the selected school
 * @returns {number|undefined} Returns 0 if no school is selected, undefined otherwise
 */
export const navigateToPreviousSchool = (currentSchool, schools, setSchool) => {
  if (currentSchool?.id == null) {
    return 0
  }

  let newIndex = schools.findIndex(s => s.id === currentSchool?.id) - 1
  if (newIndex < 0) {
    newIndex = schools.length - 1
  }

  setSchool(schools[newIndex])
}

/**
 * Navigates to the next school in the list, wrapping to the first school if at the end.
 * Extracted for testability.
 *
 * @param {Object|null} currentSchool - The currently selected school object with id property
 * @param {Array} schools - Array of school objects to navigate through
 * @param {Function} setSchool - Function to set the selected school
 * @returns {number|undefined} Returns 0 if no school is selected, undefined otherwise
 */
export const navigateToNextSchool = (currentSchool, schools, setSchool) => {
  if (currentSchool?.id == null) {
    return 0
  }

  let newIndex = schools.findIndex(s => s.id === currentSchool?.id) + 1
  if (newIndex >= schools.length) {
    newIndex = 0
  }

  setSchool(schools[newIndex])
}
import { SchoolDropdown } from "./components/dropdown"
import { AlertDetail } from "./pages/alerts/detail"
import { TabOptions } from "./pages/wireless_config/consts"
import { SparkView } from "./pages/spark/view"
import OAuthCallback from "./pages/oauth/callback"

import { AlertContext, useAlertProvider } from "./hooks/alert_context"
import { AppProvider, useAppContext } from "@ciq/shared/hooks/context"
import { GlobalIndicator } from "@ciq/shared/components/global_indicator"
import { Landing } from "@ciq/shared/components/landing"
import { Console } from "@ciq/shared/components/console"
import { ErrorMessage } from "@ciq/shared/components/error"
import { getCurrentId, NAVIGATION_OPTION_IDS } from "./utils/getCurrentId"
import { CookieConsentProvider } from "@ciq/shared/contexts/CookieConsentContext"
import { AnalyticsProvider, useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { FeatureFlagOverrideProvider } from "@ciq/shared/contexts/FeatureFlagOverrideContext"
import { FeatureFlagDevPanel } from "@ciq/shared/components/FeatureFlagDevPanel"
import { FeatureFlags } from "@ciq/shared/constants/featureFlags"
import "@ciq/shared/components/cookie_banner/CookieBanner.css"

initMap()

export const DEFAULT_DATA = {
  loading: false,
  wifiConfigDataReload: {
    state: 0,
  },
}

export const SET_LOADING = "SET_LOADING"

export const SET_NOTIFICATIONS = "SET_NOTIFICATIONS"
export const SET_USER_SETTINGS = "SET_USER_SETTINGS"

export const SET_WIFI_CONFIG_DATA_RELOAD = "SET_WIFI_CONFIG_DATA_RELOAD"

/**
 * Reducer for the App component's local state.
 * Handles loading state and wifi config reload triggers.
 *
 * **Exported for testing purposes only. Do not use externally.**
 *
 * @param {Object} state - Current state
 * @param {Object} action - Action object with type and optional payload
 * @returns {Object} New state
 * @private
 */
export const AppReducer = (state, action) => {
  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case SET_NOTIFICATIONS:
      return {
        ...state,
        ...action.payload,
      }

    case SET_USER_SETTINGS:
      return {
        ...state,
        ...action.payload,
      }

    case SET_WIFI_CONFIG_DATA_RELOAD: {
      return {
        ...state,
        wifiConfigDataReload: {
          state: action.payload,
        },
      }
    }

    default:
      throw new Error(`Unknown action type ${action.type}`)
  }
}

const NON_LOGIN_SCREEN_PATHS = ["/error"]

function App() {
  const onLogout = useCallback(() => {
    createLogoutHandler(Api, eventBus, ErrorToast)()
  }, [])

  return (
    <AppProvider>
      <CookieConsentProvider>
        <FeatureFlagOverrideProvider>
          <AnalyticsProvider>
            <IdleTimeout onLogout={onLogout} />
            <div id="map-content" />
            <Landing noUserRequiredPaths={NON_LOGIN_SCREEN_PATHS} />
            <View />
            <ToastContainer position="bottom-center" autoClose={20000} />
            <GlobalIndicator />
            <FeatureFlagDevPanel />
          </AnalyticsProvider>
        </FeatureFlagOverrideProvider>
      </CookieConsentProvider>
    </AppProvider>
  )
}

function View() {
  const {
    isSystemAdmin,
    school,
    schools,
    setSchool,
    notifications,
    setNotifications,
    hasData,
    setShowSettings,
  } = useAppContext()
  const { isFeatureEnabled } = useAnalyticsContext()
  const [state, dispatch] = useReducer(AppReducer, DEFAULT_DATA)
  const isError = window.location.pathname === "/error"
  const showFileUpload = isFeatureEnabled(FeatureFlags.floor_plans)

  const flagWifiConfigDataReload = () => dispatch({ type: SET_WIFI_CONFIG_DATA_RELOAD, payload: 1 })

  const clearWifiConfigDataReload = useCallback(() => {
    dispatch({ type: SET_WIFI_CONFIG_DATA_RELOAD, payload: 0 })
  }, [])

  const onDropdownItemSelected = useCallback(
    (optionId, navigate) => {
      switch (optionId) {
        case DROPDOWN_OPTION_IDS.HELP:
          console.debug("HELP clicked")
          break

        case DROPDOWN_OPTION_IDS.CHAT:
          console.debug("CHAT clicked")
          break

        case DROPDOWN_OPTION_IDS.SETTINGS:
          setShowSettings(true)
          break

        case DROPDOWN_OPTION_IDS.ADMIN_DASHBOARD:
          navigate(NAVIGATION_OPTION_IDS.ADMIN_DASHBOARD.getPath())
          break

        case DROPDOWN_OPTION_IDS.ALERT:
          navigate(NAVIGATION_OPTION_IDS.ALERT.getPath())
          break

        case DROPDOWN_OPTION_IDS.SPARK:
          navigate(NAVIGATION_OPTION_IDS.SPARK.getPath())
          break

        case DROPDOWN_OPTION_IDS.LOGOUT:
          createLogoutHandler(Api, eventBus, ErrorToast)()
          break

        default:
          throw new Error(`Unknown DropdownItem id ${optionId}`)
      }
    },
    [setShowSettings]
  )

  const handleOnPreviousSchool = useCallback(
    () => navigateToPreviousSchool(school, schools, setSchool),
    [school, schools, setSchool]
  )

  const handleOnNextSchool = useCallback(
    () => navigateToNextSchool(school, schools, setSchool),
    [school, schools, setSchool]
  )

  if (isError) {
    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/error" element={<ErrorMessage />} />
          </Routes>
        </BrowserRouter>
      </>
    )
  }

  if (!hasData) {
    return null
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/error" element={<ErrorMessage />} />
          <Route path="/" element={<Layout onDropdownItemSelected={onDropdownItemSelected} />}>
            <Route path="/" element={<InitialView />} />
            {isSystemAdmin && (
              <Route path={NAVIGATION_OPTION_IDS.MAP.getPath()} element={<MapView />} />
            )}
            {isSystemAdmin && (
              <Route
                path={NAVIGATION_OPTION_IDS.ADMIN_DASHBOARD.getPath()}
                element={<AdminConsole />}
              />
            )}
            {isSystemAdmin && (
              <Route path={NAVIGATION_OPTION_IDS.DASHBOARD.getPath()} element={<Console />} />
            )}
            {isSystemAdmin && (
              <Route
                path={NAVIGATION_OPTION_IDS.SCHOOL.getPath()}
                element={
                  <SchoolView
                    carouselDisabled={schools == null || schools.length <= 1}
                    onPreviousSchool={handleOnPreviousSchool}
                    onNextSchool={handleOnNextSchool}
                  />
                }
              />
            )}
            <Route
              path={NAVIGATION_OPTION_IDS.WIRELESS_CONFIG.getPath()}
              element={
                <WirelessConfigView
                  school={school}
                  reloadState={state.wifiConfigDataReload.state}
                  onReloaded={clearWifiConfigDataReload}
                />
              }
            />
            {isSystemAdmin && showFileUpload && (
              <Route
                path={NAVIGATION_OPTION_IDS.FILE_UPLOAD.getPath()}
                element={<FileUploadView />}
              />
            )}
            {isSystemAdmin && <Route path="/alert" element={<AlertsView />} />}
            {isSystemAdmin && <Route path="/alert/:alertId" element={<AlertDetail />} />}
            {isSystemAdmin && <Route path="/spark" element={<SparkView school={school} />} />}
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            {/* catch-all: /error is intentionally excluded — it has its own router tree
                in the isError branch above. This catch-all only applies to unknown paths
                in the normal (authenticated) routing tree. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        <div id="modal-content" />
        {isSystemAdmin && (
          <SettingsModal
            notifications={notifications.settings}
            onNotificationsUpdated={() => {
              Api.get(`/landing/notifications`)
                .then(response => setNotifications(response.data))
                .catch(e => {
                  if (e.handled) return
                  console.error(e.error)
                  ErrorToast("Unable to fetch notifications")
                })
            }}
            onApsUpdated={flagWifiConfigDataReload}
          />
        )}
      </BrowserRouter>
    </>
  )
}

/** @visibleForTesting */
export const InitialView = () => {
  const { isSystemAdmin } = useAppContext()
  const location = useLocation()
  const navigate = useNavigate()

  const { pathname } = location

  useEffect(() => {
    if (pathname !== "/") {
      return
    }

    const route = getInitialRoute(isSystemAdmin)
    // Preserve query params (e.g., ?settings=integrations from OAuth callback)
    const target = location.search ? `${route}${location.search}` : route
    navigate(target, { replace: true })
  }, [isSystemAdmin, navigate, pathname, location.search])

  return null
}

const NAVIGATION_OPTIONS = {
  badgeCount: 0,
  routes: [
    {
      id: NAVIGATION_OPTION_IDS.MAP.id,
      requiresSystemAdmin: true,
      label: "Map",
      icon: <MapIcon />,
    },
    {
      id: NAVIGATION_OPTION_IDS.DASHBOARD.id,
      requiresSystemAdmin: true,
      label: "Dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: NAVIGATION_OPTION_IDS.SCHOOL.id,
      requiresSystemAdmin: true,
      label: "Institution",
      icon: <SchoolIcon />,
    },
    {
      id: NAVIGATION_OPTION_IDS.FILE_UPLOAD.id,
      requiresSystemAdmin: true,
      label: "File Upload",
      icon: <FileUploadIcon />,
      featureFlag: FeatureFlags.floor_plans,
    },
    {
      id: NAVIGATION_OPTION_IDS.WIRELESS_CONFIG.id,
      requiresSystemAdmin: false,
      label: "Wireless Config",
      search: `?tab=${TabOptions[0].id}`,
      icon: <WirelessConfigIcon />,
    },
  ],
}

const getInitialRoute = isSystemAdmin => {
  if (isSystemAdmin) {
    return NAVIGATION_OPTION_IDS.SCHOOL.getPath()
  }

  return NAVIGATION_OPTION_IDS.WIRELESS_CONFIG.getPath()
}

const DROPDOWN_OPTION_IDS = {
  HELP: "help",
  CHAT: "chat",
  SETTINGS: "settings",
  ADMIN_DASHBOARD: "adminDashboard",
  ALERT: "alert",
  SPARK: "spark",
  LOGOUT: "logout",
}

const DROPDOWN_OPTIONS = [
  // [
  //   {id: DROPDOWN_OPTION_IDS.HELP, label: "Help & Tutorials", icon: <HelpIcon/>},
  //   {id: DROPDOWN_OPTION_IDS.CHAT, label: "Chat with Support", icon: <ChatIcon/>}
  // ],
  [
    {
      id: DROPDOWN_OPTION_IDS.SETTINGS,
      requiresSystemAdmin: true,
      label: "Settings",
      icon: <SettingsIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.ADMIN_DASHBOARD,
      requiresSystemAdmin: true,
      label: "Admin Dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.ALERT,
      requiresSystemAdmin: true,
      label: "Alerts",
      icon: <AlertIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.SPARK,
      requiresSystemAdmin: true,
      label: "Spark",
      icon: <AlertIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.LOGOUT,
      requiresSystemAdmin: false,
      label: "Log out",
      className: "danger-action",
      icon: <LogoutIcon />,
    },
  ],
]

function Layout({ onDropdownItemSelected }) {
  const { isSystemAdmin, notifications } = useAppContext()
  const { isFeatureEnabled } = useAnalyticsContext()
  const alertProvider = useAlertProvider()
  const location = useLocation()
  const navigate = useNavigate()
  const currentId = getCurrentId(isSystemAdmin, location)

  useEffect(() => {
    document.title = getRouteTitle(currentId)
  }, [currentId])

  const permissionFilter = o => {
    if (o.requiresSystemAdmin && !isSystemAdmin) return false
    if (o.featureFlag && !isFeatureEnabled(o.featureFlag)) return false
    return true
  }

  const navigationOptions = {
    ...{
      ...NAVIGATION_OPTIONS,
      routes: NAVIGATION_OPTIONS.routes.filter(permissionFilter),
    },
    badgeCount: notifications.count,
  }

  const buildOption = option => {
    return {
      ...option,
      badgeCount: notifications[option.id] != null ? notifications[option.id].count : 0,
    }
  }

  const dropdownOptions = DROPDOWN_OPTIONS.map(dropdown =>
    dropdown.filter(permissionFilter).map(buildOption)
  )

  const handleDropdownItemSelected = optionId => {
    onDropdownItemSelected(optionId, navigate)
  }

  return (
    <AlertContext.Provider value={alertProvider}>
      <div className="app-container">
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <header>
          <NavigationBar
            currentId={currentId}
            navigationOptions={navigationOptions}
            dropdownOptions={dropdownOptions}
            onDropdownItemSelected={handleDropdownItemSelected}
          >
            <SchoolDropdown />
          </NavigationBar>
        </header>
        <main id="main-content" tabIndex="-1">
          <Outlet />
        </main>
      </div>
    </AlertContext.Provider>
  )
}

// Exports for testing
// Note: createLogoutHandler, navigateToPreviousSchool, navigateToNextSchool are
// exported at definition with 'export const' for testability
export {
  DROPDOWN_OPTION_IDS,
  getInitialRoute,
}

export default App
